import { captureMessage } from "@sentry/react";
import {
  createRootRouteWithContext,
  createRoute,
  Navigate,
  Outlet,
  redirect,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { type Me, RULE_AREAS } from "@unbox/api";
import posthog from "posthog-js";
import { type ReactNode, useEffect } from "react";
import z from "zod/v4";
import { useMe } from "@/api/me";
import { ProductUpdatePopup } from "@/components/product-updates/product-update-popup";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { ssoErrorMessage } from "@/lib/sso";
import { AboutPage } from "@/pages/about";
import { AdsPage } from "@/pages/admin/ads-page";
import { CreateMerchantPage } from "@/pages/admin/create-merchant-page";
import { CreateWorkspacePage } from "@/pages/admin/create-workspace-page";
import { CustomersPage } from "@/pages/admin/customers-page";
import { InAppUpdatesPage } from "@/pages/admin/in-app-updates-page";
import { InvitesPage } from "@/pages/admin/invites-page";
import { Layout as AdminLayout } from "@/pages/admin/layout";
import { MerchantDetailPage } from "@/pages/admin/merchant-detail";
import { RiskFlagsPage } from "@/pages/admin/risk-flags-page";
import { RisksPage } from "@/pages/admin/risks-page";
import { UsersPage } from "@/pages/admin/users-page";
import { WorkspaceDetailPage } from "@/pages/admin/workspace-detail/workspace-detail-page";
import { AnalyticsOverview as NewAnalyticsDashboard } from "@/pages/analytics-dashboard";
import { AskPage } from "@/pages/ask";
import { ActionRequired } from "@/pages/assurance/action-required";
import { AllProcessedVideos } from "@/pages/assurance/all-processed-videos";
import { RisksToReview } from "@/pages/assurance/risks-to-review";
import { AcceptInvitation } from "@/pages/auth/accept-invitation";
import { ForgotPasswordPage } from "@/pages/auth/forgot-password";
import { ResetPasswordPage } from "@/pages/auth/reset-password";
import { AutoLogin } from "@/pages/auto-login";
import { BlockedCreatorsPage } from "@/pages/blocked-creators/blocked-creators-page";
import { AdsAuthorisationPage } from "@/pages/connect-ads/ads-authorisation-page";
import { PreAuthExplainerPage } from "@/pages/connect-ads/pre-auth-explainer-page";
import { CreatorCohortsPage } from "@/pages/creator-cohorts";
import { CreatorTagsPage } from "@/pages/creator-tags";
import { DashboardLayout } from "@/pages/dashboard-layout";
import { CandidateReviewMatches } from "@/pages/explorers/candidate-review-matches";
import { CreatorExplorer } from "@/pages/explorers/creator-explorer";
import { ProductExplorer } from "@/pages/explorers/product-explorer";
import { VideoExplorer } from "@/pages/explorers/video-explorer";
import { HomePage } from "@/pages/home";
import { Login } from "@/pages/login";
import { MessageTemplates } from "@/pages/message-templates";
import { SampleRequestsManagement } from "@/pages/operations/sample-requests";
import { Paused } from "@/pages/paused";
import { CustomerProductUpdatesPage } from "@/pages/product-updates/product-updates-page";
import { ReportDetail, Reports } from "@/pages/reports";
import { RulesPage } from "@/pages/rules";
import { SafetyConfigure } from "@/pages/safety-beta/configure";
import { SafetyFlagged } from "@/pages/safety-beta/flagged";
import { SafetyOverview } from "@/pages/safety-overview";
import { Sequences } from "@/pages/sequences";
import { AutomationBuilder } from "@/pages/sequences/automation-builder";
import { SequenceDetail } from "@/pages/sequences/sequence-detail";
import { SequenceRuns } from "@/pages/sequences/sequence-runs";
import { IntegrationsPage } from "@/pages/settings/integrations-page";
import { MerchantDetailsPage } from "@/pages/settings/merchant-details-page";
import { MyDetailsPage } from "@/pages/settings/my-details-page";
import { SecurityPage } from "@/pages/settings/security-page";
import { TeamPage } from "@/pages/settings/team-page";
import { PublicSmartBriefPage, SmartBriefDisplayPage, SmartBriefsRouter } from "@/pages/smart-briefs";
import { TwoFactorChallenge } from "@/pages/two-factor";
import { useAuth } from "@/providers/auth-provider";
import { BulkActionProgressProvider } from "@/providers/bulk-action-progress-provider";
import { ResourceRefreshProvider } from "@/providers/resource-refresh-provider";
import { SSEProvider } from "@/providers/sse-provider";
import { error as showError } from "@/providers/ToastProvider";
import { getLastAccessed, type LastAccessed, setLastAccessed } from "@/utils/last-accessed";
import { workspaceForMerchant } from "@/utils/workspace-for-merchant";
import { SentryUserContext } from "./components/sentry-user-context";
import { OpenGroupsPage } from "./pages/collab-groups/open-groups-page";
import { TargetGroupsPage } from "./pages/collab-groups/target-groups-page";
import {
  OpenCollaborationDetail,
  OpenCollaborationsPage,
  RecommendedCreatorsView,
  SampleRequestsView,
  TargetCollaborationDetail,
  TargetCollaborationGroupDetail,
  TargetCollaborationsPage,
} from "./pages/collaborations";
import { ConnectMerchantPage } from "./pages/connect-merchant/connect-merchant-page";
import { Conversations } from "./pages/conversations";
import { CandidateExplorer } from "./pages/explorers/candidate-explorer";
import { NotFound } from "./pages/not-found";
import { PortfolioAnalyticsDashboard } from "./pages/portfolio/analytics-dashboard";
import { PortfolioOverview } from "./pages/portfolio/overview";
import { PortfolioSafetyOverview } from "./pages/portfolio/safety-overview";
import { PortfolioLayout } from "./pages/portfolio-layout";
import { TikTokConnect } from "./pages/tiktok-connect";
import { AddMerchantPage } from "./pages/workspace/add-merchant-page";
import { WorkspaceMerchantsPage } from "./pages/workspace/merchants-page";
import { WorkspaceOverview } from "./pages/workspace/overview";
import { WorkspacePortfolioOverview, WorkspacePortfolioSafety } from "./pages/workspace/portfolio";
import { WorkspaceSafetyOverview } from "./pages/workspace/safety";
import { WorkspaceSafetyConfig } from "./pages/workspace/safety-config/WorkspaceSafetyConfig";
import { WorkspaceTeamPage } from "./pages/workspace/team-page";
import { WorkspaceLayout } from "./pages/workspace-layout";
import { WorkspacePortfolioLayout } from "./pages/workspace-portfolio-layout";
import { OnboardingProvider } from "./providers/onboarding-provider";
import { PortfolioProvider } from "./providers/PortfolioProvider";
import { safeRedirectPath } from "./utils/safe-redirect";

// Manually initialize PostHog
if (typeof window !== "undefined" && import.meta.env.VITE_POSTHOG_API_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_API_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || "https://us.posthog.com",
    capture_pageview: true,
    person_profiles: "identified_only",
  });
}

const enumParam = <T extends string>(values: [T, ...T[]], fallback: T) =>
  z.enum(values).catch(fallback).default(fallback);

// Parent routers
const rootRoute = createRootRouteWithContext<{ isAuthenticated: boolean | undefined; user: Me | undefined }>()({
  component: () => <Outlet />,
  notFoundComponent: NotFound,
});

const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  beforeLoad: async ({ context: { isAuthenticated, user }, location }) => {
    if (!isAuthenticated) {
      const next = `${location.pathname}${location.searchStr ?? ""}${location.hash}`;
      throw redirect({ to: "/login", search: { next } });
    }
    if (user?.role === "paused" && location.pathname !== "/paused") {
      throw redirect({ to: "/paused" });
    }
  },
  id: "_authenticated",
  component: () => (
    <PortfolioProvider>
      <OnboardingProvider>
        <SentryUserContext />
        <Outlet />
      </OnboardingProvider>
    </PortfolioProvider>
  ),
});

const reportedMerchantPathFallbacks = new Set<string>();

const merchantRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/merchant/$merchantId",
  notFoundComponent: NotFound,
  beforeLoad: ({ context, params, location }) => {
    const merchants = context.user?.merchants ?? [];
    const valid = merchants.some((m) => m.id === params.merchantId);
    if (!valid) {
      const storedId = getLastAccessed()?.merchantId;
      const fallback =
        (storedId && storedId !== params.merchantId && merchants.some((m) => m.id === storedId) ? storedId : null) ??
        merchants[0]?.id;
      if (fallback) throw redirect({ to: "/merchant/$merchantId", params: { merchantId: fallback } });
      throw redirect({ to: "/login" });
    }

    const workspaceId = workspaceForMerchant(
      context.user?.workspaces ?? [],
      params.merchantId,
      getLastAccessed()?.workspaceId,
    );
    if (workspaceId) {
      throw redirect({
        to: `/workspace/${workspaceId}${location.pathname}` as string,
        search: location.search,
        hash: location.hash,
      });
    }
    if (context.user?.role !== "admin" && !reportedMerchantPathFallbacks.has(params.merchantId)) {
      reportedMerchantPathFallbacks.add(params.merchantId);
      captureMessage("No workspace contains merchant, leaving user on merchant path", {
        level: "warning",
        tags: { merchant_id: params.merchantId },
        fingerprint: ["merchant-path-fallback"],
      });
    }
  },
  component: function MerchantLayout() {
    const { merchantId } = merchantRoute.useParams();
    useEffect(() => {
      setLastAccessed({ workspaceId: null, merchantId });
    }, [merchantId]);
    return (
      <SSEProvider>
        <ResourceRefreshProvider>
          <BulkActionProgressProvider>
            <Outlet />
          </BulkActionProgressProvider>
          <ProductUpdatePopup />
        </ResourceRefreshProvider>
      </SSEProvider>
    );
  },
});

const isOldSafetyAllowed = () =>
  posthog.isFeatureEnabled("F-allow-old-safety") ??
  (() => {
    try {
      const overrides = JSON.parse(localStorage.getItem("feature_flags") || "{}");
      return overrides["F-allow-old-safety"] === true;
    } catch {
      return false;
    }
  })();

export function resolveOverviewWorkspace({
  workspaces,
  lastAccessed,
}: {
  workspaces: Pick<NonNullable<Me["workspaces"]>[number], "id" | "role">[] | undefined;
  lastAccessed: LastAccessed | null;
}): string | null {
  const administered = (workspaces ?? []).filter((workspace) => workspace.role === "workspace_admin");
  if (lastAccessed) {
    return administered.some((workspace) => workspace.id === lastAccessed.workspaceId)
      ? lastAccessed.workspaceId
      : null;
  }
  return administered[0]?.id ?? null;
}

export type RootLanding =
  | { to: "/workspace/$workspaceId/merchant/$merchantId"; params: { workspaceId: string; merchantId: string } }
  | { to: "/merchant/$merchantId"; params: { merchantId: string } }
  | { to: "/login" };

export function resolveDefaultLanding(user: Me | null | undefined, lastAccessed: LastAccessed | null): RootLanding {
  if (lastAccessed?.workspaceId) {
    const workspace = user?.workspaces?.find((w) => w.id === lastAccessed.workspaceId);
    const validMerchant = workspace?.merchants.some((m) => m.id === lastAccessed.merchantId);
    if (workspace && validMerchant) {
      return {
        to: "/workspace/$workspaceId/merchant/$merchantId",
        params: { workspaceId: lastAccessed.workspaceId, merchantId: lastAccessed.merchantId },
      };
    }
    const firstWorkspace = user?.workspaces?.[0];
    const firstMerchant = firstWorkspace?.merchants[0];
    if (firstWorkspace && firstMerchant) {
      return {
        to: "/workspace/$workspaceId/merchant/$merchantId",
        params: { workspaceId: firstWorkspace.id, merchantId: firstMerchant.id },
      };
    }
  }

  const allMerchants = [...(user?.merchants ?? []), ...(user?.workspaces?.flatMap((w) => w.merchants) ?? [])];
  const merchantId = lastAccessed?.merchantId || allMerchants[0]?.id;
  if (merchantId) return { to: "/merchant/$merchantId", params: { merchantId } };
  return { to: "/login" };
}

export type MerchantLanding = "/home" | "/analytics/overview" | "/safety/overview";

export function resolveMerchantLanding({
  role,
  isInsights,
}: {
  role: Me["role"] | undefined;
  isInsights: boolean;
}): MerchantLanding {
  if (role !== "restricted_user") return "/home";
  return isInsights ? "/analytics/overview" : "/safety/overview";
}

// Unconditional — every old path in this map always redirects to its new equivalent,
// regardless of the nav-v2 flag (the flag only controls which nav renders the links).
export function redirectToNewPath(merchantTo: string) {
  return ({ params, search }: { params: Record<string, string>; search: Record<string, unknown> }) => {
    if ("workspaceId" in params) {
      throw redirect({ to: `/workspace/$workspaceId${merchantTo}` as string, params, search });
    }
    throw redirect({ to: merchantTo, params, search });
  };
}

// Same as `redirectToNewPath`, but for an old route whose `?tab=` query decided between two
// different new pages (e.g. `/collaborations?tab=open`) — `redirectToNewPath`'s single fixed
// destination can't express that, so the destination is resolved from `search` instead.
function redirectToNewPathFromSearch(resolvePath: (search: Record<string, unknown>) => string) {
  return ({ params, search }: { params: Record<string, string>; search: Record<string, unknown> }) => {
    const merchantTo = resolvePath(search);
    if ("workspaceId" in params) {
      throw redirect({ to: `/workspace/$workspaceId${merchantTo}` as string, params, search });
    }
    throw redirect({ to: merchantTo, params, search });
  };
}

// Workspace-scoped merchant route — same pages as merchantRoute but under /workspace/:workspaceId
const workspaceMerchantRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/workspace/$workspaceId/merchant/$merchantId",
  notFoundComponent: NotFound,
  beforeLoad: ({ context, params }) => {
    const { workspaceId, merchantId } = params;
    const user = context.user;
    const workspace = user?.workspaces?.find((w) => w.id === workspaceId);
    const validWorkspace = user?.role === "admin" || !!workspace;
    const validMerchant = user?.role === "admin" || workspace?.merchants.some((m) => m.id === merchantId);
    if (!validWorkspace || !validMerchant) {
      throw redirect({ to: "/login" });
    }
  },
  component: function WorkspaceMerchantLayout() {
    const { merchantId, workspaceId } = workspaceMerchantRoute.useParams();
    const { data: user } = useMe();
    useEffect(() => {
      setLastAccessed({ workspaceId, merchantId });
      // Report the selected workspace to PostHog so events can be filtered by workspace.
      const workspace = user?.workspaces?.find((w) => w.id === workspaceId);
      if (workspace && !user?.impersonatedBy) {
        posthog.setPersonProperties({ workspaceId: workspace.id, workspaceName: workspace.name });
      }
    }, [workspaceId, merchantId, user]);
    return (
      <SSEProvider>
        <ResourceRefreshProvider>
          <BulkActionProgressProvider>
            <Outlet />
          </BulkActionProgressProvider>
          <ProductUpdatePopup />
        </ResourceRefreshProvider>
      </SSEProvider>
    );
  },
});

// TODO: Unauthenticated routes here are doing full page reloads
const unauthenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  beforeLoad: ({ context: { isAuthenticated }, location }) => {
    if (isAuthenticated) {
      const search = new URLSearchParams(location.search);
      const ssoError = search.get("error");
      if (ssoError) {
        showError(
          "Single sign-on wasn't connected",
          { description: ssoErrorMessage(ssoError) },
          { duration: Infinity },
        );
      }
      throw redirect({ to: safeRedirectPath(search.get("next")) });
    }
  },
  id: "_unauthenticated",
  component: () => <Outlet />,
});

const adminRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  beforeLoad: ({ context: { user } }) => {
    if (user?.role !== "admin") throw redirect({ to: "/" });
  },
  path: "/admin",
  component: () => (
    <AdminLayout>
      <Outlet />
    </AdminLayout>
  ),
});

// Ask Unbox — merchant-scoped chat behind the F-ask-unbox flag. The backend
// (/merchant/:merchantId/ask/*) validates the merchant and scopes the data, so no
// extra role gate here. Direct navigation while the flag is off redirects home.
function GatedAskPage() {
  const enabled = useFeatureFlag("F-ask-unbox");
  if (!enabled) return <Navigate to="/" />;
  return <AskPage />;
}
const askRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/ask",
  component: GatedAskPage,
});

const homeRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/home",
  component: () => (
    <DashboardLayout page="home">
      <HomePage />
    </DashboardLayout>
  ),
});

const redirectRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/",
  beforeLoad: ({ context }) => {
    const lastAccessed = getLastAccessed();
    const landing = resolveDefaultLanding(context.user, lastAccessed);
    const overviewWorkspaceId = resolveOverviewWorkspace({ workspaces: context.user?.workspaces, lastAccessed });
    if (!overviewWorkspaceId) throw redirect(landing);
    throw redirect({ to: "/workspace/$workspaceId/overview", params: { workspaceId: overviewWorkspaceId } });
  },
});

const pausedRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/paused",
  beforeLoad: ({ context: { user } }) => {
    if (user?.role !== "paused") throw redirect({ to: "/" });
  },
  component: Paused,
});

// Merchant index redirect — redirects to default page based on merchant features
const merchantIndexRedirect = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/",
  beforeLoad: ({ context, params }) => {
    const allMerchants = [
      ...(context.user?.merchants ?? []),
      ...(context.user?.workspaces?.flatMap((w) => w.merchants) ?? []),
    ];
    const merchant = allMerchants.find((m) => m.id === params.merchantId);
    const landing = resolveMerchantLanding({
      role: context.user?.role,
      isInsights: !!merchant?.features?.includes("insights"),
    });
    if (landing === "/home") {
      throw redirect({
        to: "/merchant/$merchantId/home",
        params: { merchantId: params.merchantId },
      });
    }
    if (landing === "/analytics/overview") {
      throw redirect({
        to: "/merchant/$merchantId/analytics/overview",
        params: { merchantId: params.merchantId },
        search: { tab: "overview" },
      });
    }
    throw redirect({
      to: "/merchant/$merchantId/safety/overview",
      params: { merchantId: params.merchantId },
    });
  },
});

// Merchant-scoped routes

const legacyAnalyticsOverviewRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/analytics-overview",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/merchant/$merchantId/analytics/overview",
      params: { merchantId: params.merchantId },
    });
  },
});

const legacyAnalyticsDashboardRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/analytics-dashboard",
  beforeLoad: redirectToNewPath("/merchant/$merchantId/analytics/overview"),
});

const analyticsDashboardRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/analytics/overview",
  component: () => (
    <DashboardLayout page="analytics-dashboard">
      <NewAnalyticsDashboard />
    </DashboardLayout>
  ),
});

const legacySmartInsightsRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/smart-insights",
  beforeLoad: redirectToNewPath("/merchant/$merchantId/home"),
});

// Explorer routes
const filterOptionSchema = z.object({ id: z.string(), label: z.string() });

const legacyVideoExplorerRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/video-explorer",
  beforeLoad: redirectToNewPath("/merchant/$merchantId/videos"),
});

export const videoExplorerRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/videos",
  validateSearch: z.object({
    video_id: z.string().optional(),
    publishDateStart: z.string().optional(),
    publishDateEnd: z.string().optional(),
    creatorType: z.enum(["affiliate", "brand"]).optional(),
    videoRiskStatus: z.enum(["no_risk", "has_risk"]).optional(),
    riskId: z.array(z.string()).optional(),
    // Deprecated — superseded by riskId. Kept only for URL/back-compat; unused.
    riskTypeShort: z.array(z.string()).optional(),
    safetyStatus: z.array(z.string()).optional(),
    actionTaken: z.array(z.string()).optional(),
    riskTier: z.array(z.string()).optional(),
    minGmv: z.string().optional(),
    maxGmv: z.string().optional(),
    product: z.array(filterOptionSchema).optional(),
    hooks: z.array(filterOptionSchema).optional(),
    videoStyles: z.array(filterOptionSchema).optional(),
    tactics: z.array(filterOptionSchema).optional(),
    sellingPoints: z.array(filterOptionSchema).optional(),
    videoLanguages: z.array(filterOptionSchema).optional(),
    creator: z.array(filterOptionSchema).optional(),
    creatorList: z.array(filterOptionSchema).optional(),
    campaignId: z.string().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    timePeriod: z.enum(["7d", "30d", "all_time"]).optional(),
    search: z.string().optional(),
    campaign: z.array(filterOptionSchema).optional(),
    negated: z.array(z.string()).optional(),
    fromEmptyState: z.boolean().optional().catch(undefined),
  }),
  component: () => (
    <DashboardLayout page="video-explorer">
      <VideoExplorer />
    </DashboardLayout>
  ),
});

const legacyCreatorExplorerRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/creator-explorer",
  beforeLoad: redirectToNewPath("/merchant/$merchantId/creators"),
});

export const creatorExplorerRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/creators",
  validateSearch: z.object({
    creator_id: z.string().optional(),
    postedFrom: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    postedTo: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    creatorType: z.enum(["affiliate", "brand"]).optional(),
    riskTier: z.array(z.string()).optional(),
    minGmv: z.string().optional(),
    maxGmv: z.string().optional(),
    product: z.array(filterOptionSchema).optional(),
    maleAudiencePercent: z.enum([">25", ">50", ">75"]).optional(),
    femaleAudiencePercent: z.enum([">25", ">50", ">75"]).optional(),
    age: z.array(z.enum(["18-24", "25-34", "35-44", "45-54", "55+"])).optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    search: z.string().optional(),
    campaign: z.array(filterOptionSchema).optional(),
    topSellingCategories: z.array(filterOptionSchema).optional(),
    classification: z
      .enum(["recommended_creators", "top_creator", "active_partner", "dormant_creator", "safe_creator", "__all"])
      .optional(),
    listId: z.string().optional(),
    creatorLevel: z.array(z.string()).optional(),
    categoryPro: z.array(z.string()).optional(),
    creatorLanguage: z.array(z.string()).optional(),
    includeCohorts: z.array(filterOptionSchema).optional(),
    ruleArea: z.enum(RULE_AREAS).optional(),
    ruleId: z.string().optional(),
    negated: z.array(z.string()).optional(),
  }),
  component: () => (
    <DashboardLayout page="creator-explorer">
      <CreatorExplorer />
    </DashboardLayout>
  ),
});

const legacyCandidateExplorerRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/candidate-explorer",
  beforeLoad: redirectToNewPath("/merchant/$merchantId/creators/find"),
});

export const candidateExplorerRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/creators/find",
  validateSearch: z.object({
    candidate_id: z.string().optional(),
    minGmv: z.string().optional(),
    maxGmv: z.string().optional(),
    minFollowers: z.string().optional(),
    maxFollowers: z.string().optional(),
    minUnitsSold: z.string().optional(),
    maxUnitsSold: z.string().optional(),
    minRating: z.string().optional(),
    maxRating: z.string().optional(),
    gender: z.enum(["male", "female"]).optional(),
    maleAudiencePercent: z.enum([">25", ">50", ">75"]).optional(),
    femaleAudiencePercent: z.enum([">25", ">50", ">75"]).optional(),
    age: z.array(z.enum(["18-24", "25-34", "35-44", "45-54", "55+"])).optional(),
    search: z.string().optional(),
    topSellingCategories: z.array(filterOptionSchema).optional(),
    creatorLevel: z.array(z.string()).optional(),
    categoryPro: z.array(z.string()).optional(),
    creatorLanguage: z.array(z.string()).optional(),
    negated: z.array(z.string()).optional(),
  }),
  component: () => (
    <DashboardLayout page="candidate-explorer">
      <CandidateExplorer />
    </DashboardLayout>
  ),
});

const legacyProductExplorerRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/product-explorer",
  beforeLoad: redirectToNewPath("/merchant/$merchantId/products/all"),
});

export const productExplorerRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/products/all",
  validateSearch: z.object({
    product_id: z.string().optional(),
    tab: z.enum(["performance", "content-analysis", "videos", "safety"]).optional(),
    minGmv: z.string().optional(),
    maxGmv: z.string().optional(),
    productStatus: z.array(z.string()).optional(),
    creator: z.array(filterOptionSchema).optional(),
    creatorList: z.array(filterOptionSchema).optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    search: z.string().optional(),
    negated: z.array(z.string()).optional(),
  }),
  component: () => (
    <DashboardLayout page="product-explorer">
      <ProductExplorer />
    </DashboardLayout>
  ),
});

const safetyBetaSearchSchema = z.object({
  tab: z.enum(["needs-review", "with-creator", "risk-resolved"]).catch("needs-review").default("needs-review"),
  video_id: z.string().optional(),
  minViews: z.string().optional(),
  maxViews: z.string().optional(),
  publishDateStart: z.string().optional(),
  publishDateEnd: z.string().optional(),
  flaggedDateStart: z.string().optional(),
  flaggedDateEnd: z.string().optional(),
  riskId: z.array(z.string()).optional(),
  // Deprecated — superseded by riskId. Kept only for URL/back-compat; unused.
  riskTypeShort: z.array(z.string()).optional(),
  riskTier: z.array(z.string()).optional(),
  creatorType: z.enum(["affiliate", "brand"]).optional(),
  hooks: z.array(filterOptionSchema).optional(),
  tactics: z.array(filterOptionSchema).optional(),
  sellingPoints: z.array(filterOptionSchema).optional(),
  videoStyles: z.array(filterOptionSchema).optional(),
  videoLanguages: z.array(filterOptionSchema).optional(),
  campaign: z.array(filterOptionSchema).optional(),
  negated: z.array(z.string()).optional(),
});

// Configure page: `tab` is which of Risks/Policies is active. `policy` opens a single
// policy's detail view in place of the tabs; `risk` selects one or more risks within
// the Risks tab (each one's category group opens to show it) without replacing the
// tab content. `focusRisk` opens the Configure Risks modal on that risk, asking to switch it
// `focusAction` (on by default), so the link can be shared.
const safetyConfigureSearchSchema = z.object({
  tab: z.enum(["risks", "policies"]).catch("risks").default("risks"),
  policy: z.string().optional(),
  risk: z.array(z.string()).optional(),
  focusRisk: z.string().optional(),
  focusAction: z.enum(["on", "off"]).optional(),
});

const legacySafetyOverviewRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/safety-overview",
  beforeLoad: redirectToNewPath("/merchant/$merchantId/safety/overview"),
});

const safetyOverviewRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/safety/overview",
  component: () => (
    <DashboardLayout page="safety-overview">
      <SafetyOverview />
    </DashboardLayout>
  ),
});

const legacyFlaggedVideosRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/flagged-videos",
  validateSearch: safetyBetaSearchSchema,
  beforeLoad: redirectToNewPath("/merchant/$merchantId/safety/flagged"),
});

const legacyReviewQueueRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/review-queue",
  validateSearch: safetyBetaSearchSchema,
  beforeLoad: redirectToNewPath("/merchant/$merchantId/safety/flagged"),
});

export const flaggedVideosRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/safety/flagged",
  validateSearch: safetyBetaSearchSchema,
  component: () => (
    <DashboardLayout page="flagged-videos">
      <SafetyFlagged />
    </DashboardLayout>
  ),
});

const legacyConfigureSafetyRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/configure-safety",
  validateSearch: safetyConfigureSearchSchema,
  beforeLoad: redirectToNewPath("/merchant/$merchantId/safety/configure"),
});

export const configureSafetyRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/safety/configure",
  validateSearch: safetyConfigureSearchSchema,
  component: () => (
    <DashboardLayout page="configure-safety">
      <SafetyConfigure />
    </DashboardLayout>
  ),
});

// The alpha `/safety-beta*` routes were deleted outright in the safety-alpha removal (no redirect
// left behind), so a direct/bookmarked hit 404s today. These four exist purely to forward to the
// permanent v2 pages at their equivalent path.
const legacySafetyBetaRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/safety-beta",
  beforeLoad: redirectToNewPath("/merchant/$merchantId/safety/flagged"),
});

const legacySafetyBetaOverviewRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/safety-beta/overview",
  beforeLoad: redirectToNewPath("/merchant/$merchantId/safety/overview"),
});

const legacySafetyBetaFlaggedRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/safety-beta/flagged",
  validateSearch: safetyBetaSearchSchema,
  beforeLoad: redirectToNewPath("/merchant/$merchantId/safety/flagged"),
});

const legacySafetyBetaConfigureRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/safety-beta/configure",
  validateSearch: safetyConfigureSearchSchema,
  beforeLoad: redirectToNewPath("/merchant/$merchantId/safety/configure"),
});

export const legacyRisksToReviewRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/risks-to-review",
  validateSearch: z.object({
    tab: z
      .enum(["pendingManualReview", "manuallyReviewed"])
      .catch("pendingManualReview")
      .default("pendingManualReview"),
    video_id: z.string().optional(),
    riskId: z.array(z.string()).optional(),
    // Deprecated — superseded by riskId. Kept only for URL/back-compat; unused.
    riskTypeShort: z.array(z.string()).optional(),
    riskTier: z.array(z.string()).optional(),
    creatorType: z.enum(["affiliate", "brand"]).optional(),
    publishDateStart: z.string().optional(),
    publishDateEnd: z.string().optional(),
    search: z.string().optional(),
    product: z.array(filterOptionSchema).optional(),
    negated: z.array(z.string()).optional(),
  }),
  beforeLoad: (opts) => {
    if (isOldSafetyAllowed()) return;
    redirectToNewPath("/merchant/$merchantId/safety/flagged")(opts);
  },
  component: () => (
    <DashboardLayout page="risks-to-review">
      <RisksToReview />
    </DashboardLayout>
  ),
});

export const legacyActionRequiredRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/action-required",
  validateSearch: z.object({
    tab: z
      .enum(["awaitingAction", "actionInProgress", "loggedAsComplete"])
      .catch("awaitingAction")
      .default("awaitingAction"),
    video_id: z.string().optional(),
    riskId: z.array(z.string()).optional(),
    // Deprecated — superseded by riskId. Kept only for URL/back-compat; unused.
    riskTypeShort: z.array(z.string()).optional(),
    riskTier: z.array(z.string()).optional(),
    creatorType: z.enum(["affiliate", "brand"]).optional(),
    actionTaken: z.array(z.string()).optional(),
    publishDateStart: z.string().optional(),
    publishDateEnd: z.string().optional(),
    search: z.string().optional(),
    negated: z.array(z.string()).optional(),
  }),
  beforeLoad: (opts) => {
    if (isOldSafetyAllowed()) return;
    redirectToNewPath("/merchant/$merchantId/safety/flagged")(opts);
  },
  component: () => (
    <DashboardLayout page="action-required">
      <ActionRequired />
    </DashboardLayout>
  ),
});

export const legacyAllProcessedVideosRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/all-processed-videos",
  validateSearch: z.object({
    tab: z.enum(["risksDetected", "deemedSafe", "unableToAnalyse"]).catch("risksDetected").default("risksDetected"),
    video_id: z.string().optional(),
    riskId: z.array(z.string()).optional(),
    // Deprecated — superseded by riskId. Kept only for URL/back-compat; unused.
    riskTypeShort: z.array(z.string()).optional(),
    riskTier: z.array(z.string()).optional(),
    creatorType: z.enum(["affiliate", "brand"]).optional(),
    publishDateStart: z.string().optional(),
    publishDateEnd: z.string().optional(),
    search: z.string().optional(),
    negated: z.array(z.string()).optional(),
  }),
  beforeLoad: (opts) => {
    if (isOldSafetyAllowed()) return;
    redirectToNewPath("/merchant/$merchantId/safety/flagged")(opts);
  },
  component: () => (
    <DashboardLayout page="all-processed-videos">
      <AllProcessedVideos />
    </DashboardLayout>
  ),
});

export const sequencesRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/automate-outreach",
  validateSearch: z.object({
    tab: z.enum(["defaults", "all"]).optional(),
  }),
  component: () => (
    <DashboardLayout page="sequences">
      <Sequences />
    </DashboardLayout>
  ),
});

// The redesigned creator outreach builder. `$triggerType` is "new" while creating, since the
// automation is identified by the trigger it binds and no trigger is chosen yet.
export const automationBuilderRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/automate-outreach/automation/$triggerType",
  validateSearch: z.object({
    fromEmptyState: z.boolean().optional().catch(undefined),
  }),
  component: function AutomationBuilderPage() {
    const { triggerType } = useParams({ strict: false }) as { triggerType: string };
    const { fromEmptyState } = useSearch({ strict: false }) as { fromEmptyState?: boolean };
    return (
      <DashboardLayout page="sequences">
        <AutomationBuilder triggerParam={triggerType} fromEmptyState={fromEmptyState} />
      </DashboardLayout>
    );
  },
});

const sequenceDetailRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/automate-outreach/$sequenceId",
  validateSearch: z.object({
    setDefaultFor: z.enum(["safety_action", "collaboration_invite", "sample_delivered", "creator_dormant"]).optional(),
  }),
  component: function SequenceDetailPage() {
    const { sequenceId } = useParams({ strict: false }) as { sequenceId: string };
    const { setDefaultFor } = useSearch({ strict: false }) as {
      setDefaultFor?: "safety_action" | "collaboration_invite" | "sample_delivered" | "creator_dormant";
    };
    return (
      <DashboardLayout page="sequences">
        <SequenceDetail sequenceId={sequenceId} setDefaultFor={setDefaultFor} />
      </DashboardLayout>
    );
  },
});

export const sequenceRunsRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/automate-outreach/runs",
  validateSearch: z.object({
    search: z.string().optional(),
    sequenceRunSequence: z.string().optional(),
    sequenceRunStatus: z.enum(["active", "completed", "cancelled", "failed"]).optional(),
    negated: z.array(z.string()).optional(),
  }),
  component: () => (
    <DashboardLayout page="sequence-runs">
      <SequenceRuns />
    </DashboardLayout>
  ),
});

export const SAMPLE_REQUEST_VIEWS = ["product", "sku", "creator"] as const;
export const SAMPLE_REQUEST_TABS = ["pending", "approved", "rejected", "ignored", "expired"] as const;

const legacySampleRequestsRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/samples",
  beforeLoad: redirectToNewPath("/merchant/$merchantId/collabs/samples"),
});

export const sampleRequestsRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/collabs/samples",
  validateSearch: z.object({
    view: enumParam([...SAMPLE_REQUEST_VIEWS], "product"),
    tab: enumParam([...SAMPLE_REQUEST_TABS], "pending"),
  }),
  component: () => (
    <DashboardLayout page="sample-requests">
      <SampleRequestsManagement />
    </DashboardLayout>
  ),
});

export const creatorTagsRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/tags",
  component: () => (
    <DashboardLayout page="creator-tags">
      <CreatorTagsPage />
    </DashboardLayout>
  ),
});

const legacyCreatorCohortsRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/cohorts",
  beforeLoad: redirectToNewPath("/merchant/$merchantId/creators/cohorts"),
});

export const creatorCohortsRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/creators/cohorts",
  validateSearch: z.object({
    manageCohorts: z.boolean().optional().catch(undefined),
  }),
  component: () => (
    <DashboardLayout page="creator-cohorts">
      <CreatorCohortsPage />
    </DashboardLayout>
  ),
});

export const rulesRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/creators/rules",
  component: () => (
    <DashboardLayout page="rules">
      <RulesPage />
    </DashboardLayout>
  ),
});

const legacyBlockedCreatorsRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/blocked-creators",
  beforeLoad: redirectToNewPath("/merchant/$merchantId/creators/blocked"),
});

export const blockedCreatorsRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/creators/blocked",
  validateSearch: z.object({
    creator_id: z.string().optional(),
  }),
  component: () => (
    <DashboardLayout page="blocked-creators">
      <BlockedCreatorsPage />
    </DashboardLayout>
  ),
});

const legacyMessageTemplatesRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/message-templates",
  beforeLoad: redirectToNewPath("/merchant/$merchantId/creators/message-templates"),
});

export const messageTemplatesRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/creators/message-templates",
  component: () => (
    <DashboardLayout page="message-templates">
      <MessageTemplates />
    </DashboardLayout>
  ),
});

export const settingsIntegrationsRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/settings/integrations",
  component: () => (
    <DashboardLayout page="integrations">
      <IntegrationsPage />
    </DashboardLayout>
  ),
});

export const settingsMerchantDetailsRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/settings/merchant-details",
  component: () => (
    <DashboardLayout page="merchant-details">
      <MerchantDetailsPage />
    </DashboardLayout>
  ),
});

export const settingsTeamRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/settings/team",
  validateSearch: z.object({ sort: z.enum(["role"]).optional().catch(undefined) }),
  component: () => (
    <DashboardLayout page="team">
      <TeamPage />
    </DashboardLayout>
  ),
});

function OwnAccountPage({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (user?.impersonatedBy) return <Navigate to="/" />;
  return children;
}

export const settingsMyDetailsRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/settings/my-details",
  component: () => (
    <OwnAccountPage>
      <DashboardLayout page="my-details">
        <MyDetailsPage />
      </DashboardLayout>
    </OwnAccountPage>
  ),
});

export const settingsSecurityRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/settings/security",
  component: () => (
    <OwnAccountPage>
      <DashboardLayout page="security">
        <SecurityPage />
      </DashboardLayout>
    </OwnAccountPage>
  ),
});

const customerProductUpdatesRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/product-updates",
  component: () => (
    <DashboardLayout page="product-updates">
      <CustomerProductUpdatesPage />
    </DashboardLayout>
  ),
});

const legacyReportsIndexRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/reports",
  beforeLoad: redirectToNewPath("/merchant/$merchantId/analytics/reports"),
});

const reportsIndexRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/analytics/reports",
  component: () => (
    <DashboardLayout page="reports">
      <Reports />
    </DashboardLayout>
  ),
});

const legacyReportsDetailRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/reports/$reportId",
  beforeLoad: ({ params, search }: { params: Record<string, string>; search: Record<string, unknown> }) => {
    if ("workspaceId" in params) {
      throw redirect({
        to: "/workspace/$workspaceId/merchant/$merchantId/analytics/reports/$reportId" as string,
        params: { workspaceId: params.workspaceId, merchantId: params.merchantId, reportId: params.reportId },
        search,
      });
    }
    throw redirect({
      to: "/merchant/$merchantId/analytics/reports/$reportId",
      params: { merchantId: params.merchantId, reportId: params.reportId },
      search,
    });
  },
});

export const reportsDetailRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/analytics/reports/$reportId",
  component: () => (
    <DashboardLayout page="reports">
      <ReportDetail />
    </DashboardLayout>
  ),
});

// Smart Briefs Routes
const legacySmartBriefsRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/smart-briefs",
  beforeLoad: redirectToNewPath("/merchant/$merchantId/products/briefs"),
});

export const smartBriefsRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/products/briefs",
  validateSearch: z.object({
    productId: z.string().optional(),
    productName: z.string().optional(),
  }),
  component: () => (
    <DashboardLayout page="smart-briefs">
      <SmartBriefsRouter />
    </DashboardLayout>
  ),
});

// Legacy routes - redirect to main smart-briefs page
const smartBriefsListRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/smart-briefs/list",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/merchant/$merchantId/smart-briefs",
      params: { merchantId: params.merchantId },
    });
  },
});

// View brief page
const legacySmartBriefViewRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/smart-briefs/brief/$briefId",
  beforeLoad: ({ params, search }: { params: Record<string, string>; search: Record<string, unknown> }) => {
    if ("workspaceId" in params) {
      throw redirect({
        to: "/workspace/$workspaceId/merchant/$merchantId/products/briefs/brief/$briefId" as string,
        params: { workspaceId: params.workspaceId, merchantId: params.merchantId, briefId: params.briefId },
        search,
      });
    }
    throw redirect({
      to: "/merchant/$merchantId/products/briefs/brief/$briefId",
      params: { merchantId: params.merchantId, briefId: params.briefId },
      search,
    });
  },
});

const smartBriefViewRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/products/briefs/brief/$briefId",
  component: function SmartBriefView() {
    const { briefId, merchantId } = useParams({ strict: false }) as {
      briefId: string;
      merchantId: string;
    };
    const nav = useNavigate();
    return (
      <DashboardLayout page="smart-briefs">
        <SmartBriefDisplayPage
          briefId={briefId}
          onBack={() => nav({ to: "/merchant/$merchantId/products/briefs", params: { merchantId } })}
        />
      </DashboardLayout>
    );
  },
});

// Legacy edit route - redirect to view page
const smartBriefEditRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/smart-briefs/edit/$briefId",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/merchant/$merchantId/smart-briefs/brief/$briefId",
      params: { merchantId: params.merchantId, briefId: params.briefId },
    });
  },
});

// Public share route (no authentication required)
const publicShareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/share/$shareId",
  component: () => {
    const { shareId } = publicShareRoute.useParams();
    return <PublicSmartBriefPage shareId={shareId} />;
  },
});

// Reachable signed in or out, since it exists to diagnose failed sign-ins
const aboutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/about",
  component: AboutPage,
});

// Ads authorization — public pages the customer lands on outside the app. They hang off
// rootRoute rather than unauthenticatedRoute, which bounces signed-in users away.
const connectAdsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/connect/ads",
  validateSearch: z.object({ merchant_id: z.string().optional() }),
  component: function ConnectAdsEntry() {
    const { merchant_id: merchantId } = connectAdsRoute.useSearch();
    return <PreAuthExplainerPage merchantId={merchantId} />;
  },
});

const connectAdsAuthorisationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/connect/ads/authorisation/$authorisationId",
  component: function ConnectAdsSession() {
    const { authorisationId } = connectAdsAuthorisationRoute.useParams();
    return <AdsAuthorisationPage authorisationId={authorisationId} />;
  },
});

const connectMerchantRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/connect/merchant/$shareToken",
  component: function ConnectMerchant() {
    const { shareToken } = connectMerchantRoute.useParams();
    return <ConnectMerchantPage shareToken={shareToken} />;
  },
});

// Portfolio routes

// NOTE: If you're adding something here, it needs to start with /portfolio so we show the right logo
// See src/components/sidebar/merchant-selector.tsx
const portfolioAnalyticsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/portfolio-analytics",
  component: () => (
    <PortfolioLayout page="portfolio-analytics">
      <PortfolioAnalyticsDashboard />
    </PortfolioLayout>
  ),
});

const portfolioOverviewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/portfolio/overview",
  component: () => (
    <PortfolioLayout page="portfolio-overview">
      <PortfolioOverview />
    </PortfolioLayout>
  ),
});

const portfolioSafetyRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/portfolio-safety",
  component: () => (
    <PortfolioLayout page="portfolio-safety">
      <PortfolioSafetyOverview />
    </PortfolioLayout>
  ),
});

// Workspace routes (UNB-1953) — Workspace Admins' equivalent of the portfolio routes
// above, scoped to a single workspace rather than every brand the user can access.
//
// Access (workspace_admin role) is checked by
// `useWorkspaceAdmin()` inside `WorkspaceLayout` (UNB-2694), not here — that lets a
// denied or nonexistent workspace render the same in-chrome no-access empty state
// instead of a route-level redirect.

const workspaceOverviewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/workspace/$workspaceId/overview",
  component: () => (
    <WorkspaceLayout page="workspace-overview">
      <WorkspaceOverview />
    </WorkspaceLayout>
  ),
});

const workspaceSafetyRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/workspace/$workspaceId/safety",
  component: () => (
    <WorkspaceLayout page="workspace-safety">
      <WorkspaceSafetyOverview />
    </WorkspaceLayout>
  ),
});

const workspaceSafetyConfigRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/workspace/$workspaceId/safety/default-settings",
  component: () => (
    <WorkspaceLayout page="workspace-safety-config">
      <WorkspaceSafetyConfig />
    </WorkspaceLayout>
  ),
});

const workspaceTeamRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/workspace/$workspaceId/settings/team",
  component: () => (
    <WorkspaceLayout page="workspace-team">
      <WorkspaceTeamPage />
    </WorkspaceLayout>
  ),
});

export const workspaceMerchantsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/workspace/$workspaceId/settings/merchants",
  validateSearch: z.object({ added: z.string().optional() }),
  component: () => (
    <WorkspaceLayout page="workspace-merchants">
      <WorkspaceMerchantsPage />
    </WorkspaceLayout>
  ),
});

const workspaceAddMerchantRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/workspace/$workspaceId/settings/merchants/new",
  component: () => (
    <WorkspaceLayout page="workspace-merchants">
      <AddMerchantPage />
    </WorkspaceLayout>
  ),
});

const workspacePortfolioOverviewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/workspace/$workspaceId/portfolio/overview",
  component: () => (
    <WorkspacePortfolioLayout page="workspace-portfolio-overview">
      <WorkspacePortfolioOverview />
    </WorkspacePortfolioLayout>
  ),
});

const workspacePortfolioSafetyRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/workspace/$workspaceId/portfolio/safety",
  component: () => (
    <WorkspacePortfolioLayout page="workspace-portfolio-safety">
      <WorkspacePortfolioSafety />
    </WorkspacePortfolioLayout>
  ),
});

export const adminIndexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/",
  component: () => <Navigate to="/admin/customers" />,
});

export const adminMerchantsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/merchants",
  component: () => <Navigate to="/admin/customers" />,
});

export const adminCreateMerchantRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/merchants/new",
  validateSearch: z.object({
    openId: z.coerce.string().optional(),
    shopId: z.coerce.string().optional(),
  }),
  component: CreateMerchantPage,
});

export const adminMerchantDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/merchants/$id",
  component: MerchantDetailPage,
});

export const adminUsersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/users",
  component: UsersPage,
});

export const adminRiskFlagsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/risk-flags",
  component: RiskFlagsPage,
});

export const adminRisksRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/risks",
  component: RisksPage,
});

export const adminInvitesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/invites",
  component: InvitesPage,
});

export const adminInAppUpdatesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/in-app-updates",
  validateSearch: z.object({
    tab: z.enum(["product-updates", "new-page-notification"]).catch("product-updates").default("product-updates"),
  }),
  component: InAppUpdatesPage,
});

const adminProductUpdatesRedirectRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/product-updates",
  beforeLoad: () => {
    throw redirect({ to: "/admin/in-app-updates", search: { tab: "product-updates" } });
  },
});

export const adminCustomersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/customers",
  // `catch` keeps a hand-edited or stale URL from failing validation outright.
  validateSearch: z.object({
    tab: z.enum(["workspaces", "brands"]).catch("workspaces").default("workspaces"),
    page: z.coerce.number().int().min(1).catch(1).default(1),
  }),
  component: CustomersPage,
});

export const adminAdsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/ads",
  component: AdsPage,
});

export const adminWorkspaceDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/customers/$id",
  component: WorkspaceDetailPage,
});

export const adminCreateWorkspaceRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/workspaces/new",
  component: CreateWorkspacePage,
});

export const adminCatchAllRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/*",
  component: () => <Navigate to="/admin/customers" />,
});

export const loginRoute = createRoute({
  getParentRoute: () => unauthenticatedRoute,
  path: "/login",
  validateSearch: z.object({
    email: z.string().optional(),
    password: z.string().optional(),
    next: z.string().optional(),
    error: z.string().optional(),
  }),
  component: Login,
});

export const twoFactorRoute = createRoute({
  getParentRoute: () => unauthenticatedRoute,
  path: "/login/two-factor",
  validateSearch: z.object({ next: z.string().optional(), link: z.string().optional() }),
  component: TwoFactorChallenge,
});

export const autoLoginRoute = createRoute({
  getParentRoute: () => unauthenticatedRoute,
  path: "/auto-login",
  validateSearch: z.object({
    email: z.string().optional(),
    password: z.string().optional(),
  }),
  component: AutoLogin,
});

// Auth routes
export const acceptInviteRoute = createRoute({
  getParentRoute: () => unauthenticatedRoute,
  path: "/auth/accept-invite",
  validateSearch: z.object({
    token: z
      .union([z.string(), z.number()])
      .transform((val) => String(val))
      .optional(),
  }),
  beforeLoad: ({ search }) => {
    if (!search.token)
      throw redirect({
        to: "/login",
      });
  },
  component: AcceptInvitation,
});
export const forgotPasswordRoute = createRoute({
  getParentRoute: () => unauthenticatedRoute,
  path: "/auth/forgot-password",
  component: ForgotPasswordPage,
});

export const resetPasswordRoute = createRoute({
  getParentRoute: () => unauthenticatedRoute,
  // TODO: This is referenced in api/src/resources/auth/routes.ts
  path: "/auth/reset-password",
  validateSearch: z.object({
    email: z.string().optional(),
    token: z
      .union([z.string(), z.number()])
      .transform((val) => String(val))
      .optional(),
  }),
  beforeLoad: ({ search }) => {
    if (!search.token)
      throw redirect({
        to: "/login",
      });
  },
  component: ResetPasswordPage,
});

// TikTok Shop OAuth route
const tiktokConnectRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/tiktok-connect",
  component: TikTokConnect,
});

// Collaborations route
export const collaborationsRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/collaborations",
  validateSearch: z.object({
    tab: z.enum(["target", "open"]).default("target"),
  }),
  beforeLoad: redirectToNewPathFromSearch((search) =>
    search.tab === "open" ? "/merchant/$merchantId/collabs/open" : "/merchant/$merchantId/collabs/target",
  ),
});

const collabGroupsRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/collab-groups",
  validateSearch: z.object({
    tab: z.enum(["target", "open"]).default("target"),
  }),
  beforeLoad: redirectToNewPathFromSearch((search) =>
    search.tab === "open" ? "/merchant/$merchantId/collabs/groups/open" : "/merchant/$merchantId/collabs/groups/target",
  ),
});

const collabsTargetRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/collabs/target",
  component: () => (
    <DashboardLayout page="collabs-target">
      <TargetCollaborationsPage />
    </DashboardLayout>
  ),
});

const collabsOpenRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/collabs/open",
  component: () => (
    <DashboardLayout page="collabs-open">
      <OpenCollaborationsPage />
    </DashboardLayout>
  ),
});

const collabGroupsTargetRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/collabs/groups/target",
  component: () => (
    <DashboardLayout page="collab-groups-target">
      <TargetGroupsPage />
    </DashboardLayout>
  ),
});

const collabGroupsOpenRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/collabs/groups/open",
  component: () => (
    <DashboardLayout page="collab-groups-open">
      <OpenGroupsPage />
    </DashboardLayout>
  ),
});

const creatorStatusTabSchema = z.enum([
  "all",
  "removed",
  "invited",
  "sample_requested",
  "sample_status",
  "pending_post",
  "posted_video",
  "generated_gmv",
  "posted_no_gmv",
  "cancelled",
]);

// Target Collaboration detail route
export const targetCollaborationDetailRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/collaborations/target/$collaborationId",
  validateSearch: z.object({
    tab: creatorStatusTabSchema.catch("all").default("all"),
  }),
  component: () => (
    <DashboardLayout page="collaborations">
      <TargetCollaborationDetail />
    </DashboardLayout>
  ),
});

// Target Collaboration recommendations route
export const targetCollaborationRecommendationsRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/collaborations/target/$collaborationId/recommendations",
  validateSearch: z.object({
    // When coming from a Smart Group, groupId carries all sibling collaborationIds
    groupId: z.string().optional(),
  }),
  component: () => (
    <DashboardLayout page="collaborations">
      <RecommendedCreatorsView mode="collaboration" />
    </DashboardLayout>
  ),
});

// Target Collaboration sample requests review route
export const targetCollaborationSampleRequestsRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/collaborations/target/$collaborationId/sample-requests",
  component: () => (
    <DashboardLayout page="collaborations">
      <SampleRequestsView />
    </DashboardLayout>
  ),
});

// Explorer-level recommendations review route (no collaboration context)
export const reviewRecommendationsRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/review-recommendations",
  validateSearch: z.object({
    type: z.enum(["creator", "candidate"]).default("creator"),
  }),
  component: function ReviewRecommendationsPage() {
    const { type = "creator" } = useSearch({ strict: false }) as {
      type?: "creator" | "candidate";
    };
    return (
      <DashboardLayout page={type === "creator" ? "creator-explorer" : "candidate-explorer"}>
        {type === "candidate" ? (
          <CandidateReviewMatches />
        ) : (
          <RecommendedCreatorsView mode="explorer" explorerType="creator" />
        )}
      </DashboardLayout>
    );
  },
});

// Target Collaboration group detail route
export const targetCollaborationGroupDetailRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/collaborations/target-group/$groupId",
  validateSearch: z.object({
    tab: creatorStatusTabSchema.catch("all").default("all"),
    // Set when navigating here from a real smart_collab_groups (v2) card, not the legacy
    // hash-grouping "Smart Unbox Group" flow — the page reuses everything but a couple of
    // labels that only make sense for one mechanism or the other.
    source: z.enum(["smart-collab-group"]).optional(),
  }),
  component: () => (
    <DashboardLayout page="collaborations">
      <TargetCollaborationGroupDetail />
    </DashboardLayout>
  ),
});

// Open Collaboration detail route
export const openCollaborationDetailRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/collaborations/open/$collaborationId",
  validateSearch: z.object({
    tab: z
      .enum(["all", "sample_requested", "sample_status", "pending_post", "posted_no_gmv", "generated_gmv", "cancelled"])
      .catch("all")
      .default("all"),
  }),
  component: () => (
    <DashboardLayout page="collaborations">
      <OpenCollaborationDetail />
    </DashboardLayout>
  ),
});

// Open Collaboration sample requests review route
export const openCollaborationSampleRequestsRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/collaborations/open/$collaborationId/sample-requests",
  component: () => (
    <DashboardLayout page="collaborations">
      <SampleRequestsView collaborationType="open" />
    </DashboardLayout>
  ),
});

// Legacy collaboration detail route (backwards compatibility - defaults to Target)
export const collaborationDetailRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/collaborations/$collaborationId",
  validateSearch: z.object({
    tab: creatorStatusTabSchema.catch("all").default("all"),
  }),
  component: () => (
    <DashboardLayout page="collaborations">
      <TargetCollaborationDetail />
    </DashboardLayout>
  ),
});

// Interactions route
const legacyInteractionsRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/activities",
  beforeLoad: redirectToNewPath("/merchant/$merchantId/creators/messages"),
});

export const interactionsRoute = createRoute({
  getParentRoute: () => merchantRoute,
  path: "/creators/messages",
  validateSearch: z.object({
    activityType: z.enum(["all", "messages", "creator_activity", "brand_activity"]).optional(),
  }),
  component: () => (
    <DashboardLayout page="activities">
      <Conversations />
    </DashboardLayout>
  ),
});

// Legacy catch-all for old URLs without /merchant/$merchantId prefix
// Legacy redirect routes — redirect old paths (without /merchant/$merchantId prefix) to new paths
export const legacyMerchantPaths = [
  "/analytics-dashboard",
  "/analytics-overview",
  "/smart-insights",
  "/video-explorer",
  "/creator-explorer",
  "/candidate-explorer",
  "/product-explorer",
  "/safety-overview",
  "/risks-to-review",
  "/action-required",
  "/all-processed-videos",
  "/safety-beta",
  "/safety-beta/overview",
  "/safety-beta/flagged",
  "/safety-beta/configure",
  "/review-queue",
  "/automate-outreach",
  "/automate-outreach/automation/$triggerType",
  "/automate-outreach/$sequenceId",
  "/automate-outreach/runs",
  "/message-templates",
  "/samples",
  "/reports",
  "/reports/$reportId",
  "/smart-briefs",
  "/smart-briefs/list",
  "/smart-briefs/brief/$briefId",
  "/smart-briefs/edit/$briefId",
  "/collaborations",
  "/collab-groups",
  "/collabs/target",
  "/collabs/open",
  "/collabs/groups/target",
  "/collabs/groups/open",
  "/collaborations/target/$collaborationId",
  "/collaborations/target/$collaborationId/recommendations",
  "/collaborations/target/$collaborationId/sample-requests",
  "/collaborations/target-group/$groupId",
  "/collaborations/open/$collaborationId",
  "/collaborations/open/$collaborationId/sample-requests",
  "/collaborations/$collaborationId",
  "/review-recommendations",
  "/activities",
  "/product-updates",
  "/tiktok-connect",
  "/ask",
  "/home",
  "/flagged-videos",
  "/configure-safety",
  "/tags",
  "/cohorts",
  "/blocked-creators",
  "/settings/integrations",
  "/settings/merchant-details",
  "/settings/team",
  "/settings/my-details",
  "/settings/security",
  "/safety/overview",
  "/safety/flagged",
  "/safety/configure",
  "/creators",
  "/creators/cohorts",
  "/creators/rules",
  "/creators/blocked",
  "/creators/find",
  "/creators/messages",
  "/creators/message-templates",
  "/collabs/samples",
  "/analytics/overview",
  "/analytics/reports",
  "/analytics/reports/$reportId",
  "/videos",
  "/products/all",
  "/products/briefs",
  "/products/briefs/brief/$briefId",
] as const;

// UNB-2309 nav restructure (PR2): the bare-URL redirect above defaulted to forwarding to the
// same suffix it matched (just adding the merchant/workspace prefix). These old bare paths now
// forward to a different, newer suffix instead — the fully-prefixed old real page
// (`/merchant/$merchantId/safety-overview` etc.) is untouched and keeps serving its content
// directly. Paths with no entry here (e.g. `/automate-outreach`) keep redirecting to themselves.
export const LEGACY_PATH_REDIRECT_TARGETS: Partial<Record<(typeof legacyMerchantPaths)[number], string>> = {
  "/safety-overview": "/safety/overview",
  "/smart-insights": "/home",
  "/flagged-videos": "/safety/flagged",
  "/configure-safety": "/safety/configure",
  "/safety-beta": "/safety/flagged",
  "/safety-beta/overview": "/safety/overview",
  "/safety-beta/flagged": "/safety/flagged",
  "/safety-beta/configure": "/safety/configure",
  "/review-queue": "/safety/flagged",
  "/creator-explorer": "/creators",
  "/cohorts": "/creators/cohorts",
  "/blocked-creators": "/creators/blocked",
  "/candidate-explorer": "/creators/find",
  "/activities": "/creators/messages",
  "/message-templates": "/creators/message-templates",
  "/samples": "/collabs/samples",
  "/analytics-dashboard": "/analytics/overview",
  "/reports": "/analytics/reports",
  "/reports/$reportId": "/analytics/reports/$reportId",
  "/video-explorer": "/videos",
  "/product-explorer": "/products/all",
  "/smart-briefs": "/products/briefs",
  "/smart-briefs/brief/$briefId": "/products/briefs/brief/$briefId",
};

// `/collaborations` and `/collab-groups` pick their new target based on the `?tab=` search
// param instead of a static suffix, so they can't go in the map above.
export const LEGACY_COLLAB_TAB_REDIRECT_TARGETS = {
  "/collaborations": { target: "/collabs/target", open: "/collabs/open" },
  "/collab-groups": { target: "/collabs/groups/target", open: "/collabs/groups/open" },
} as const;

const legacyRedirectRoutes = legacyMerchantPaths.map((path) =>
  createRoute({
    getParentRoute: () => authenticatedRoute,
    path,
    beforeLoad: ({ context, params, search }) => {
      const workspaces = context.user?.workspaces ?? [];
      const last = getLastAccessed();

      // Prefer the last workspace/merchant pair the user was actually on — but only trust
      // its workspaceId if that workspace still owns the merchant (stale localStorage from
      // a since-removed grant shouldn't send anyone into a workspace they can't see).
      let merchantId = last?.merchantId;
      let workspaceId =
        last?.workspaceId &&
        workspaces.some((w) => w.id === last.workspaceId && w.merchants.some((m) => m.id === merchantId))
          ? last.workspaceId
          : null;

      if (!merchantId) {
        // No stored context — fall back to the user's first direct merchant, or (if they have
        // none of their own) the first merchant of their first workspace.
        merchantId = context.user?.merchants?.[0]?.id;
        workspaceId = null;
        if (!merchantId) {
          const ws = workspaces.find((w) => w.merchants.length > 0);
          merchantId = ws?.merchants[0]?.id;
          workspaceId = ws?.id ?? null;
        }
      }

      if (!merchantId) {
        throw redirect({ to: "/login" });
      }

      let targetSuffix: string = LEGACY_PATH_REDIRECT_TARGETS[path] ?? path;
      let targetSearch = search;

      if (path === "/collaborations" || path === "/collab-groups") {
        const tab = (search as { tab?: string }).tab === "open" ? "open" : "target";
        targetSuffix = LEGACY_COLLAB_TAB_REDIRECT_TARGETS[path][tab];
        const { tab: _tab, ...rest } = search as Record<string, unknown>;
        targetSearch = rest;
      }

      if (workspaceId) {
        throw redirect({
          to: `/workspace/$workspaceId/merchant/$merchantId${targetSuffix}` as string,
          params: { ...params, workspaceId, merchantId },
          search: targetSearch,
          // biome-ignore lint/suspicious/noExplicitAny: legacy redirect — dynamic search shape
        } as any);
      }
      throw redirect({
        to: `/merchant/$merchantId${targetSuffix}` as string,
        params: { ...params, merchantId },
        search: targetSearch,
        // biome-ignore lint/suspicious/noExplicitAny: legacy redirect — dynamic search shape
      } as any);
    },
  }),
);

// ---------------------------------------------------------------------------
// Workspace-scoped child routes — generated from merchant route definitions.
// To add a new page route: add it to SHARED_MERCHANT_ROUTES below and it will
// automatically appear under both /merchant/... and /workspace/.../merchant/...
// ---------------------------------------------------------------------------

// Workspace-aware redirect routes (need to build a URL with the right prefix)
const wsMerchantIndexRedirect = createRoute({
  getParentRoute: () => workspaceMerchantRoute,
  path: "/",
  beforeLoad: ({ context, params }) => {
    const { workspaceId, merchantId } = params;
    const allMerchants = [
      ...(context.user?.merchants ?? []),
      ...(context.user?.workspaces?.flatMap((w) => w.merchants) ?? []),
    ];
    const merchant = allMerchants.find((m) => m.id === merchantId);
    const landing = resolveMerchantLanding({
      role: context.user?.role,
      isInsights: !!merchant?.features?.includes("insights"),
    });
    if (landing === "/home") {
      throw redirect({
        to: "/workspace/$workspaceId/merchant/$merchantId/home" as string,
        params: { workspaceId, merchantId },
      });
    }
    if (landing === "/analytics/overview") {
      throw redirect({
        to: "/workspace/$workspaceId/merchant/$merchantId/analytics/overview" as string,
        params: { workspaceId, merchantId },
        // biome-ignore lint/suspicious/noExplicitAny: workspace redirect search cast
        search: { tab: "overview" } as any,
      });
    }
    throw redirect({
      to: "/workspace/$workspaceId/merchant/$merchantId/safety/overview" as string,
      params: { workspaceId, merchantId },
    });
  },
});

const wsLegacyAnalyticsOverviewRoute = createRoute({
  getParentRoute: () => workspaceMerchantRoute,
  path: "/analytics-overview",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/workspace/$workspaceId/merchant/$merchantId/analytics/overview" as string,
      params: { workspaceId: params.workspaceId, merchantId: params.merchantId },
    });
  },
});

const wsSmartBriefsListRoute = createRoute({
  getParentRoute: () => workspaceMerchantRoute,
  path: "/smart-briefs/list",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/workspace/$workspaceId/merchant/$merchantId/smart-briefs" as string,
      params: { workspaceId: params.workspaceId, merchantId: params.merchantId },
    });
  },
});

const wsSmartBriefEditRoute = createRoute({
  getParentRoute: () => workspaceMerchantRoute,
  path: "/smart-briefs/edit/$briefId",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/workspace/$workspaceId/merchant/$merchantId/smart-briefs/brief/$briefId" as string,
      params: { workspaceId: params.workspaceId, merchantId: params.merchantId, briefId: params.briefId },
    });
  },
});

// All remaining merchant page routes — adding a new route to this list automatically
// creates both the /merchant/... and /workspace/.../merchant/... versions.
const SHARED_MERCHANT_ROUTES = [
  analyticsDashboardRoute,
  legacySmartInsightsRoute,
  videoExplorerRoute,
  creatorExplorerRoute,
  candidateExplorerRoute,
  productExplorerRoute,
  safetyOverviewRoute,
  legacyRisksToReviewRoute,
  legacyActionRequiredRoute,
  legacyAllProcessedVideosRoute,
  flaggedVideosRoute,
  configureSafetyRoute,
  sequencesRoute,
  sequenceRunsRoute,
  automationBuilderRoute,
  sequenceDetailRoute,
  sampleRequestsRoute,
  messageTemplatesRoute,
  creatorTagsRoute,
  creatorCohortsRoute,
  rulesRoute,
  blockedCreatorsRoute,
  settingsIntegrationsRoute,
  settingsMerchantDetailsRoute,
  settingsTeamRoute,
  settingsMyDetailsRoute,
  settingsSecurityRoute,
  customerProductUpdatesRoute,
  reportsIndexRoute,
  reportsDetailRoute,
  smartBriefsRoute,
  smartBriefViewRoute,
  tiktokConnectRoute,
  collaborationsRoute,
  collabGroupsRoute,
  collabsTargetRoute,
  collabsOpenRoute,
  collabGroupsTargetRoute,
  collabGroupsOpenRoute,
  targetCollaborationDetailRoute,
  targetCollaborationRecommendationsRoute,
  targetCollaborationSampleRequestsRoute,
  openCollaborationSampleRequestsRoute,
  reviewRecommendationsRoute,
  targetCollaborationGroupDetailRoute,
  openCollaborationDetailRoute,
  collaborationDetailRoute,
  interactionsRoute,
  askRoute,
  homeRoute,
  legacyAnalyticsDashboardRoute,
  legacyVideoExplorerRoute,
  legacyCreatorExplorerRoute,
  legacyCandidateExplorerRoute,
  legacyProductExplorerRoute,
  legacySafetyOverviewRoute,
  legacyFlaggedVideosRoute,
  legacyReviewQueueRoute,
  legacyConfigureSafetyRoute,
  legacySafetyBetaRoute,
  legacySafetyBetaOverviewRoute,
  legacySafetyBetaFlaggedRoute,
  legacySafetyBetaConfigureRoute,
  legacySampleRequestsRoute,
  legacyCreatorCohortsRoute,
  legacyBlockedCreatorsRoute,
  legacyMessageTemplatesRoute,
  legacyReportsIndexRoute,
  legacyReportsDetailRoute,
  legacySmartBriefsRoute,
  legacySmartBriefViewRoute,
  legacyInteractionsRoute,
];

const workspaceChildRoutes = SHARED_MERCHANT_ROUTES.map(
  // biome-ignore lint/suspicious/noExplicitAny: TanStack Router's options type doesn't expose path uniformly
  (r: any) =>
    createRoute({
      getParentRoute: () => workspaceMerchantRoute,
      path: r.options.path as string,
      validateSearch: r.options.validateSearch,
      beforeLoad: r.options.beforeLoad,
      component: r.options.component,
    }),
);

// The full set of merchantRoute's direct children — extracted (rather than inlined into
// routeTree below) so a test can walk it and check every real merchant page against
// `legacyMerchantPaths`, catching a new page that forgot to add itself to that list.
export const merchantChildRoutes = [
  merchantIndexRedirect,
  analyticsDashboardRoute,
  legacyAnalyticsOverviewRoute,
  legacySmartInsightsRoute,
  videoExplorerRoute,
  creatorExplorerRoute,
  candidateExplorerRoute,
  productExplorerRoute,
  safetyOverviewRoute,
  legacyRisksToReviewRoute,
  legacyActionRequiredRoute,
  legacyAllProcessedVideosRoute,
  flaggedVideosRoute,
  configureSafetyRoute,
  smartBriefsRoute,
  smartBriefsListRoute,
  smartBriefViewRoute,
  smartBriefEditRoute,
  sequencesRoute,
  sequenceRunsRoute,
  automationBuilderRoute,
  sequenceDetailRoute,
  messageTemplatesRoute,
  settingsIntegrationsRoute,
  settingsMerchantDetailsRoute,
  settingsTeamRoute,
  settingsMyDetailsRoute,
  settingsSecurityRoute,
  sampleRequestsRoute,
  creatorTagsRoute,
  creatorCohortsRoute,
  rulesRoute,
  blockedCreatorsRoute,
  customerProductUpdatesRoute,
  reportsIndexRoute,
  reportsDetailRoute,
  tiktokConnectRoute,
  collaborationsRoute,
  collabGroupsRoute,
  collabsTargetRoute,
  collabsOpenRoute,
  collabGroupsTargetRoute,
  collabGroupsOpenRoute,
  targetCollaborationDetailRoute,
  targetCollaborationRecommendationsRoute,
  targetCollaborationSampleRequestsRoute,
  openCollaborationSampleRequestsRoute,
  reviewRecommendationsRoute,
  targetCollaborationGroupDetailRoute,
  openCollaborationDetailRoute,
  collaborationDetailRoute,
  interactionsRoute,
  askRoute,
  homeRoute,
  legacyAnalyticsDashboardRoute,
  legacyVideoExplorerRoute,
  legacyCreatorExplorerRoute,
  legacyCandidateExplorerRoute,
  legacyProductExplorerRoute,
  legacySafetyOverviewRoute,
  legacyFlaggedVideosRoute,
  legacyReviewQueueRoute,
  legacyConfigureSafetyRoute,
  legacySafetyBetaRoute,
  legacySafetyBetaOverviewRoute,
  legacySafetyBetaFlaggedRoute,
  legacySafetyBetaConfigureRoute,
  legacySampleRequestsRoute,
  legacyCreatorCohortsRoute,
  legacyBlockedCreatorsRoute,
  legacyMessageTemplatesRoute,
  legacyReportsIndexRoute,
  legacyReportsDetailRoute,
  legacySmartBriefsRoute,
  legacySmartBriefViewRoute,
  legacyInteractionsRoute,
];

export const routeTree = rootRoute.addChildren([
  authenticatedRoute.addChildren([
    redirectRoute,
    pausedRoute,
    merchantRoute.addChildren(merchantChildRoutes),
    workspaceMerchantRoute.addChildren([
      wsMerchantIndexRedirect,
      wsLegacyAnalyticsOverviewRoute,
      wsSmartBriefsListRoute,
      wsSmartBriefEditRoute,
      ...workspaceChildRoutes,
    ]),
    portfolioSafetyRoute,
    portfolioAnalyticsRoute,
    portfolioOverviewRoute,
    workspaceOverviewRoute,
    workspaceSafetyRoute,
    workspaceSafetyConfigRoute,
    workspaceTeamRoute,
    workspaceMerchantsRoute,
    workspaceAddMerchantRoute,
    workspacePortfolioOverviewRoute,
    workspacePortfolioSafetyRoute,
    adminRoute.addChildren([
      adminIndexRoute,
      adminCustomersRoute,
      adminAdsRoute,
      adminWorkspaceDetailRoute,
      adminCreateWorkspaceRoute,
      adminMerchantsRoute,
      adminCreateMerchantRoute,
      adminMerchantDetailRoute,
      adminUsersRoute,
      adminRiskFlagsRoute,
      adminRisksRoute,
      adminInvitesRoute,
      adminInAppUpdatesRoute,
      adminProductUpdatesRedirectRoute,
      adminCatchAllRoute,
    ]),
    // Legacy redirects for old paths without /merchant/$merchantId prefix
    ...legacyRedirectRoutes,
  ]),
  unauthenticatedRoute.addChildren([
    loginRoute,
    twoFactorRoute,
    autoLoginRoute,
    acceptInviteRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
  ]),

  publicShareRoute,
  aboutRoute,
  connectAdsRoute,
  connectAdsAuthorisationRoute,
  connectMerchantRoute,
]);
